import { Controller } from "@hotwired/stimulus"

// The award picker on the school-facing "New Design" page: search + quicklink
// categories over a grid of award cards, then the design options for whichever
// award was chosen.
//
// The options stay closed until an award is picked, so this controller owns
// both halves -- opening the panel is the same event as selecting the award,
// and splitting them would mean one controller reaching into another's DOM.
// Paging the color carousel is NOT here: each set owns that
// (award_color_slider_controller), so one award's paging can't move another's.
//
// Everything downstream is unchanged: the choices land in the hidden fields the
// form already submits.
export default class extends Controller {
  static targets = [
    "search", "category", "card", "selection", "empty",
    "options", "optionsTitle", "colorSelection", "colorSet", "colorCard",
    "preview", "previewImage", "previewCaption"
  ]

  connect() {
    this.activeCategoryId = "all"

    // Restore the selection on load -- most commonly a re-render after a
    // validation error. The hidden fields already carry the right ids (Rails
    // re-populates them from the model), but nothing marked the card selected
    // or re-opened the options, so the page looked like the choice had been
    // lost even though it would still submit correctly. keepColor preserves
    // the color the school actually chose instead of snapping back to the first.
    if (!this.selectionTarget.value) return

    const selected = this.selectedAwardCard()
    if (selected) selected.classList.add("award-catalog__card--selected")

    this.openOptions({ keepColor: true })
  }

  filter() {
    const term = this.searchTarget.value.trim().toLowerCase()
    let visibleCount = 0

    this.cardTargets.forEach((card) => {
      const matchesTitle = !term || card.dataset.title.includes(term)
      const matchesCategory = this.activeCategoryId === "all" || card.dataset.categoryId === this.activeCategoryId
      const visible = matchesTitle && matchesCategory

      card.hidden = !visible
      if (visible) visibleCount += 1
    })

    if (this.hasEmptyTarget) this.emptyTarget.hidden = visibleCount > 0
  }

  selectCategory(event) {
    this.activeCategoryId = event.currentTarget.dataset.categoryId

    this.categoryTargets.forEach((button) => {
      button.classList.toggle("is-active", button === event.currentTarget)
    })

    this.filter()
  }

  selectAward(event) {
    const card = event.currentTarget
    const awardId = card.dataset.awardId
    const sameAward = this.selectionTarget.value === awardId
    const wasClosed = this.optionsTarget.hidden

    this.selectionTarget.value = awardId
    this.cardTargets.forEach((c) => {
      c.classList.toggle("award-catalog__card--selected", c === card)
    })

    // Switching awards invalidates the color: variants belong to one award, and
    // the server rejects a mismatched pair outright.
    this.openOptions({ keepColor: sameAward })

    // Only on the first pick. Scrolling on every click would yank the page
    // around while someone is comparing awards.
    if (wasClosed) this.optionsTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  selectColor(event) {
    this.applyColor(event.currentTarget)
  }

  // Reads the award out of the hidden field rather than taking it as an
  // argument: the field IS the answer being submitted, and a parameter that has
  // to agree with it is a second source of truth for the same fact.
  openOptions({ keepColor } = {}) {
    const awardId = this.selectionTarget.value
    this.optionsTarget.hidden = false

    if (this.hasOptionsTitleTarget) this.optionsTitleTarget.textContent = this.awardTitle()

    this.colorSetTargets.forEach((set) => {
      set.hidden = set.dataset.awardId !== awardId
    })

    // Preselect a color so there is always a valid answer -- the leftmost one,
    // which is where the carousel starts.
    const cards = this.colorCardsFor(awardId)
    const kept = keepColor && cards.find((c) => c.dataset.colorId === this.colorSelectionTarget.value)
    this.applyColor(kept || cards[0])
  }

  applyColor(chosen) {
    this.colorSelectionTarget.value = chosen ? chosen.dataset.colorId : ""

    // Clear across EVERY set, not only the award on screen. Marking just this
    // award's cards leaves the previous award's pick still flagged in its
    // hidden set, so two cards claim to be the selection at once -- invisible,
    // but wrong for anything that reads the DOM for the chosen color.
    this.colorCardTargets.forEach((c) => {
      c.classList.toggle("award-colors__card--selected", c === chosen)
    })

    this.showPreview(chosen)
  }

  // The bigger look at what is currently picked: the chosen color's artwork,
  // or the award's own when it has no colors uploaded. One img for the panel,
  // so only the selected preview is ever downloaded -- a preview per variant
  // would put every colorway's 700px copy on the page.
  showPreview(chosen) {
    if (!this.hasPreviewTarget) return

    const source = chosen || this.selectedAwardCard()
    const url = source && source.dataset.previewUrl
    const caption = this.previewCaption(chosen)

    // Nothing uploaded yet -- hide the pane rather than show a broken image.
    // src AND caption are both cleared: leaving either behind means the pane
    // still describes the previous selection, which is what shows through if
    // it is ever revealed again before the next artwork loads.
    this.previewImageTarget.removeAttribute("src")
    this.previewImageTarget.alt = ""
    if (this.hasPreviewCaptionTarget) this.previewCaptionTarget.textContent = url ? caption : ""

    this.previewTarget.hidden = !url
    if (!url) return

    this.previewImageTarget.src = url
    this.previewImageTarget.alt = caption
  }

  previewCaption(chosen) {
    const award = this.awardTitle()
    const color = chosen && chosen.dataset.colorName

    return color ? `${award} — ${color}` : award
  }

  selectedAwardCard() {
    return this.cardTargets.find((c) => c.dataset.awardId === this.selectionTarget.value)
  }

  awardTitle() {
    const card = this.selectedAwardCard()
    const title = card && card.querySelector(".award-catalog__card-title")

    return title ? title.textContent.trim() : ""
  }

  colorCardsFor(awardId) {
    return this.colorCardTargets.filter((card) => card.dataset.awardId === awardId)
  }
}
